import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../components/ProgressBar';
import Footer from '../components/Footer';
import { loadState, updatePage } from '../utils/stateManager';
import './Page2Discovery.css';

function Page2Discovery() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    subscription: '',
    resourceGroup: '',
    vnetName: '',
    vnetResourceGroup: '',
    subnetName: ''
  });

  useEffect(() => {
    // Start with blank fields — user types or clicks "Fill Example Data"
    updatePage('page2Discovery', formData);
  }, []);

  const handleChange = (field, value) => {
    const newData = { ...formData, [field]: value };

    // When subscription changes, always regenerate suggested values
    if (field === 'subscription' && value) {
      const cleaned = value.replace(/[^a-zA-Z0-9]/g, '');
      newData.resourceGroup = `rg-${cleaned}`;
      newData.vnetName = `vnet-${cleaned}`;
      newData.vnetResourceGroup = `rg-${cleaned}-network`;
      newData.subnetName = 'subnet-default';
    }

    setFormData(newData);
    updatePage('page2Discovery', newData);
  };

  const handleSampleData = () => {
    const sampleData = {
      subscription: 'Your Company\'s Really long name',
      resourceGroup: 'rg-YourCompanysReallylongname',
      vnetName: 'vnet-YourCompanysReallylongname',
      vnetResourceGroup: 'rg-YourCompanysReallylongname-network',
      subnetName: 'subnet-default'
    };
    setFormData(sampleData);
    updatePage('page2Discovery', sampleData);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleContinue = () => {
    if (formData.subscription && formData.resourceGroup && formData.vnetName && formData.subnetName) {
      navigate('/page3');
    } else {
      alert('Please fill in all required fields');
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Azure VM Deployment Wizard</h1>
        <ProgressBar currentPage={2} totalPages={9} />
      </div>

      <div className="page-content">
        <div className="section-header">
          <h2>Step 2: Discover Existing Azure Resources</h2>
        </div>

        <div className="why-section">
          <h3>🤔 Why This Step?</h3>
          <p>We need to find your existing VNet and Subnet so the VM can connect to your network. These commands are <strong>safe (read-only)</strong> and only retrieve information.</p>
          <p className="run-from">Run these commands from: <strong>Azure Cloud Shell</strong>, <strong>VS Code (authenticated)</strong>, or <strong>Azure CLI</strong></p>
        </div>

        <div className="sample-data-section">
          <button className="btn-sample" onClick={handleSampleData}>
            📝 Fill Example Data (for testing)
          </button>
        </div>

        <div className="cli-section">
          <h3>1️⃣ Get Subscription Name</h3>
          <div className="cli-command">
            <code>az account show --query name -o tsv</code>
            <button onClick={() => copyToClipboard('az account show --query name -o tsv')}>Copy</button>
          </div>
          <div className="form-group">
            <label>Subscription Name:</label>
            <input
              type="text"
              value={formData.subscription}
              onChange={(e) => handleChange('subscription', e.target.value)}
              placeholder="Paste output here..."
            />
          </div>
        </div>

        <div className="cli-section">
          <h3>2️⃣ List Resource Groups</h3>
          <div className="cli-command">
            <code>az group list --query "[].name" -o tsv</code>
            <button onClick={() => copyToClipboard('az group list --query "[].name" -o tsv')}>Copy</button>
          </div>
          <div className="form-group">
            <label>Resource Group (where VM will be created):</label>
            <input
              type="text"
              value={formData.resourceGroup}
              onChange={(e) => handleChange('resourceGroup', e.target.value)}
              placeholder="e.g., rg-production-vms"
            />
          </div>
        </div>

        <div className="cli-section">
          <h3>3️⃣ List Virtual Networks</h3>
          <div className="cli-command">
            <code>az network vnet list --query "[].&#123;Name:name, ResourceGroup:resourceGroup&#125;" -o table</code>
            <button onClick={() => copyToClipboard('az network vnet list --query "[].{Name:name, ResourceGroup:resourceGroup}" -o table')}>Copy</button>
          </div>
          <div className="form-group">
            <label>VNet Name:</label>
            <input
              type="text"
              value={formData.vnetName}
              onChange={(e) => handleChange('vnetName', e.target.value)}
              placeholder="e.g., vnet-production"
            />
          </div>
          <div className="form-group">
            <label>VNet Resource Group:</label>
            <input
              type="text"
              value={formData.vnetResourceGroup}
              onChange={(e) => handleChange('vnetResourceGroup', e.target.value)}
              placeholder="e.g., rg-network"
            />
          </div>
        </div>

        <div className="cli-section">
          <h3>4️⃣ List Subnets</h3>
          <div className="cli-command">
            <code>az network vnet subnet list -g &#123;vnetRG&#125; --vnet-name &#123;vnet&#125; --query "[].name" -o tsv</code>
            <button onClick={() => copyToClipboard('az network vnet subnet list -g {vnetRG} --vnet-name {vnet} --query "[].name" -o tsv')}>Copy</button>
          </div>
          <p className="note">Replace &#123;vnetRG&#125; with your VNet Resource Group and &#123;vnet&#125; with your VNet Name from above</p>
          <div className="form-group">
            <label>Subnet Name:</label>
            <input
              type="text"
              value={formData.subnetName}
              onChange={(e) => handleChange('subnetName', e.target.value)}
              placeholder="e.g., subnet-vms"
            />
          </div>
        </div>
      </div>

      <div className="page-actions">
        <button className="btn-secondary" onClick={handleBack}>
          ← Back
        </button>
        <button className="btn-primary" onClick={handleContinue}>
          Next: VM Configuration →
        </button>
      </div>

      <Footer />
    </div>
  );
}

export default Page2Discovery;
